import { Button } from "@/components/ui/Button";
import { Check, MessageCircle } from "lucide-react";
import { Patient } from "../../types";
import { useCallback } from "react";
import { useUpdateCaptureCache } from "../../hooks/useUpdateCaptureCache";
import useSMSSentButtonFeedback from "./useSMSSentButtonFeedback";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import { useInitiateCaptureLink } from "@/features/card-capture/staff/hooks/useInitiateCaptureLink";
import isAdult from "../../utils/isAdult";

interface Props {
  className?: string;
  surface: "HomeScreen" | "SideModal";
  patient: Pick<
    Patient,
    "practiceId" | "patientId" | "cardCapture" | "phone" | "dateOfBirth"
  >;
}

const RequestCardViaSMSBlock = ({ className, surface, patient }: Props) => {
  const { applySentFeedback, clearSentFeedback, sentFeedbackApplied } =
    useSMSSentButtonFeedback();
  const { initiate, isLoading: isInitiating } = useInitiateCaptureLink(
    patient?.practiceId ?? "",
    patient?.patientId ?? "",
  );

  const updateCache = useUpdateCaptureCache(patient?.patientId ?? "");

  const handleSubmit = useCallback(async () => {
    const response = await initiate();
    clearSentFeedback();

    trackEvent(AnalyticsEvent.SMSCardCapture, {
      surface,
      patient_id: patient.patientId,
    });

    updateCache(response);
    applySentFeedback();
  }, [
    initiate,
    applySentFeedback,
    clearSentFeedback,
    updateCache,
    surface,
    patient.patientId,
  ]);

  if (!patient.phone || !isAdult(patient.dateOfBirth)) return null;

  return (
    <>
      {sentFeedbackApplied ? (
        <Button
          size="lg"
          color="blue"
          loading={isInitiating}
          className={className}
          onClick={handleSubmit}
          icon={Check}
        >
          SMS Sent
        </Button>
      ) : (
        <Button
          size="lg"
          loading={isInitiating}
          className={className}
          onClick={handleSubmit}
          icon={MessageCircle}
        >
          Request via SMS
        </Button>
      )}
    </>
  );
};

export default RequestCardViaSMSBlock;
